/**
 * ShareCampaignSection - Reusable component for sharing campaigns
 * Includes short link, QR code, and social share buttons
 */
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Collapse,
    Divider,
} from '@mui/material';
import {
    ContentCopy,
    Check,
    Download,
    QrCode2,
    Share,
} from '@mui/icons-material';
import { FaWhatsapp, FaTwitter, FaFacebook } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import COLORS from '../../../../../constants/colors';
import baseURL from '../../config';

const ShareCampaignSection = ({ campaignId, campaignName }) => {
    const [shortUrl, setShortUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const qrRef = useRef(null);

    // Campaign page URL (fallback)
    const campaignUrl = `${window.location.origin}/campaigns/details?id=${campaignId}`;

    useEffect(() => {
        const fetchShortLink = async () => {
            try {
                const res = await fetch(`${baseURL}/short-link/${campaignId}`);
                if (res.ok) {
                    const data = await res.json();
                    setShortUrl(data.shortUrl);
                } else {
                    // Fallback to full URL
                    setShortUrl(campaignUrl);
                }
            } catch (err) {
                console.error('Failed to fetch short link:', err);
                setShortUrl(campaignUrl);
            } finally {
                setLoading(false);
            }
        };

        if (campaignId) {
            fetchShortLink();
        }
    }, [campaignId, campaignUrl]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shortUrl);
            setCopied(true);
            toast.success('Link copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('Failed to copy link');
        }
    };

    const handleDownloadQR = () => {
        const svg = qrRef.current?.querySelector('svg');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width * 2;
            canvas.height = img.height * 2;
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            const pngUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `campaign-qr-${campaignId}.png`;
            link.href = pngUrl;
            link.click();
            toast.success('QR code downloaded!');
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    // Social share URLs
    const shareText = `Support this campaign: ${campaignName}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} - ${shortUrl}`)}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shortUrl)}`;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shortUrl)}`;

    return (
        <Box sx={{
            p: 3,
            bgcolor: '#f8f9fa',
            borderRadius: 2,
            border: '1px solid #e9ecef',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Share sx={{ color: COLORS.primary, fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={600} color={COLORS.primary}>
                    Share Campaign
                </Typography>
            </Box>

            {/* Short Link Section */}
            <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Campaign Link
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        value={loading ? 'Loading...' : shortUrl}
                        InputProps={{
                            readOnly: true,
                            sx: {
                                bgcolor: '#fff',
                                borderRadius: 1.5,
                                fontSize: '0.85rem',
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                                        <IconButton
                                            size="small"
                                            onClick={handleCopy}
                                            disabled={loading}
                                            sx={{ color: copied ? 'success.main' : COLORS.primary }}
                                        >
                                            {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>

            {/* Social Share Buttons */}
            <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Share on Social Media
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Share on WhatsApp">
                        <IconButton
                            onClick={() => window.open(whatsappUrl, '_blank')}
                            sx={{
                                bgcolor: '#25D366',
                                color: '#fff',
                                '&:hover': { bgcolor: '#1da851' },
                                width: 40,
                                height: 40,
                            }}
                        >
                            <FaWhatsapp size={18} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Share on Twitter">
                        <IconButton
                            onClick={() => window.open(twitterUrl, '_blank')}
                            sx={{
                                bgcolor: '#1DA1F2',
                                color: '#fff',
                                '&:hover': { bgcolor: '#0d8ddb' },
                                width: 40,
                                height: 40,
                            }}
                        >
                            <FaTwitter size={18} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Share on Facebook">
                        <IconButton
                            onClick={() => window.open(facebookUrl, '_blank')}
                            sx={{
                                bgcolor: '#4267B2',
                                color: '#fff',
                                '&:hover': { bgcolor: '#365899' },
                                width: 40,
                                height: 40,
                            }}
                        >
                            <FaFacebook size={18} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={showQR ? 'Hide QR Code' : 'Show QR Code'}>
                        <IconButton
                            onClick={() => setShowQR(!showQR)}
                            sx={{
                                bgcolor: showQR ? COLORS.primary : '#6c757d',
                                color: '#fff',
                                '&:hover': { bgcolor: showQR ? COLORS.secondary : '#5a6268' },
                                width: 40,
                                height: 40,
                            }}
                        >
                            <QrCode2 fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* QR Code Section */}
            <Collapse in={showQR}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: '#fff',
                    borderRadius: 2,
                }}>
                    <Box ref={qrRef} sx={{ p: 2, bgcolor: '#fff', borderRadius: 2 }}>
                        <QRCodeSVG
                            value={shortUrl || campaignUrl}
                            size={160}
                            level="H"
                            includeMargin
                            fgColor={COLORS.primary}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 2, textAlign: 'center' }}>
                        Scan to visit campaign page
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={handleDownloadQR}
                        sx={{
                            textTransform: 'none',
                            borderColor: COLORS.primary,
                            color: COLORS.primary,
                            '&:hover': {
                                borderColor: COLORS.primary,
                                bgcolor: `${COLORS.primary}10`,
                            }
                        }}
                    >
                        Download QR Code
                    </Button>
                </Box>
            </Collapse>
        </Box>
    );
};

export default ShareCampaignSection;
